import { useEffect, useState } from 'react';
import { ISidebarLink } from '../../types';

import {
  AddMoneyMenuIcon,
  AddMoneyMenuIconSelected,
  AiIcon,
  AiIconSelected,
  AppCenterIcon,
  AppCenterIconSelected,
  BalanceUpMenuIcon,
  BalanceUpMenuIconSelected,
  BankingStatementsIcon,
  BankingStatementsIconSelected,
  BillPayMenuIcon,
  BillPayMenuIconSelected,
  DashboardMenuIcon,
  DashboardMenuIconSelected,
  InvoicesMenuIcon,
  InvoicesMenuIconSelected,
  LiliCardMenuIcon,
  LiliCardMenuIconSelected,
  SendMoneyMenuIcon,
  SendMoneyMenuIconSelected,
  TransactionsMenuIcon,
  TransactionsMenuIconSelected,
} from '../../assets/svg';
import { sendBankLetterService } from './services/bankLetter';
import { FEATURE_FLAG } from '../../constants/general';
import {
  validateAccountantAi,
  validateAccountingReports,
  validateAddMoney,
  validateBillPay,
  validateClientsAndProductsHub,
  validateForm1065,
  validateForm1120s,
  validateForm1120,
  validateFormScheduleC,
  validateInvoices,
  validateRoutingNumberChange,
  validateSendMoney,
} from './services/service';
import { trackMixPanel } from '../../services/mixpanel.service';
import { eventBus } from '../../services/eventBus';
import { MoveMoneyTypes } from '../../pages/MoveMoney/consts';
import useStore from '../../store';
import { LimitedFeatureType } from '../../services/appAccess.service';

export const useMainSideBarData = () => {
  const { isLoadingInitialData } = useStore((state) => state.moveMoneyData);
  const generateLinks = () => [
    {
      linkName: 'Dashboard',
      icon: DashboardMenuIcon,
      iconSelected: DashboardMenuIconSelected,
      path: '/dashboard',
      id: 'dashboard',
      limitedFeatureType: LimitedFeatureType.DashboardWidgetsAndAccountBalance,
    },
    {
      linkName: 'My Lili Card',
      icon: LiliCardMenuIcon,
      iconSelected: LiliCardMenuIconSelected,
      path: '/my-lili-card',
      id: 'my-lili-card',
      limitedFeatureType: LimitedFeatureType.CardActions,
    },
    {
      linkName: 'Transactions',
      icon: TransactionsMenuIcon,
      iconSelected: TransactionsMenuIconSelected,
      path: '/transactions',
      id: 'transactions',
      limitedFeatureType: LimitedFeatureType.TransactionsListView,
    },
    {
      linkName: 'Add Money',
      icon: AddMoneyMenuIcon,
      iconSelected: AddMoneyMenuIconSelected,
      shouldValidateMasterLink: { func: () => validateAddMoney() },
      path: '/',
      id: 'ach-transfer',
      subLinkDropdownIds: [
        'ach-transfer',
        'check-deposit',
        'add-money',
        'wire-transfer',
        'international-wire-transfer',
        'cash-deposit',
      ],
      subLinks: [
        {
          name: 'ACH Transfer',
          path: '/ach-transfer',
          id: 'ach-transfer',
          limitedFeatureType: LimitedFeatureType.AddMoneyACHDirectDeposit,
        },
        {
          name: 'Wire Transfer',
          path: '/wire-transfer',
          id: 'wire-transfer',
          limitedFeatureType: LimitedFeatureType.AddMoneyIncomingWireTransfer,
        },
        {
          name: 'International Wire Transfer',
          path: '/international-wire-transfer',
          id: 'international-wire-transfer',
          featureFlagId: FEATURE_FLAG.INTERNATIONAL_WIRE_IN_TRANSFER,
          limitedFeatureType: LimitedFeatureType.AddMoneyIncomingWireTransfer,
        },
        {
          name: 'Cash Deposit',
          path: '/cash-deposit',
          id: 'cash-deposit',
          limitedFeatureType: LimitedFeatureType.AddMoneyCashDeposit,
        },
        {
          name: 'Check Deposit',
          path: '/check-deposit',
          id: 'check-deposit',
          limitedFeatureType: LimitedFeatureType.AddMoneyCheckDeposit,
        },
        {
          name: 'Transfer from a Linked Account',
          path: '/add-money',
          id: 'add-money',
          limitedFeatureType: LimitedFeatureType.AddMoneyACHLinkedAccount,
        },
      ],
    },
    {
      linkName: 'Send Money',
      shouldValidateMasterLink: { func: () => validateSendMoney() },
      icon: SendMoneyMenuIcon,
      iconSelected: SendMoneyMenuIconSelected,
      path: '/send-money',
      id: 'send-money',
      subLinkDropdownIds: ['send-money', 'bank-transfer', 'add-a-bill'],
      subLinks: [
        {
          name: 'Make a Bank Transfer',
          path: '/move-money/ach-out-transfer',
          id: 'ach-out-transfer',
          limitedFeatureType: LimitedFeatureType.SendMoneyACH,
          featureFlagId: FEATURE_FLAG.NEW_ACH_OUT,
          analyticsCb: () => {
            eventBus.emit('moveTypeChanged', MoveMoneyTypes.ACH_OUT_TRANSFER);
          },
          disabled: isLoadingInitialData,
        },
        {
          name: 'Pay a Bill',
          path: '/bills/add',
          id: 'add-a-bill',
          featureFlagId: FEATURE_FLAG.BILL_PAY,
          limitedFeatureType: LimitedFeatureType.SendMoneyPayBill,
          upgradeToUseFeature: true,
          analyticsCb: () => {
            trackMixPanel('Act on “pay a bill” move money > send money', {
              Action: 'Pay a bill',
            });
          },
          disabled: isLoadingInitialData,
        },
        {
          name: 'Mail a Check',
          path: '/move-money/mail-check',
          id: 'mail-a-check',
          limitedFeatureType: LimitedFeatureType.SendMoneyMailACheck,
          featureFlagId: FEATURE_FLAG.MAIL_CHECK,
          analyticsCb: () => {
            eventBus.emit('moveTypeChanged', MoveMoneyTypes.MAIL_CHECK);
          },
          disabled: isLoadingInitialData,
        },
        {
          name: 'Domestic Wire Transfer',
          path: '/move-money/wire-out',
          id: 'move-money',
          limitedFeatureType: LimitedFeatureType.SendMoneyWireOut,
          featureFlagId: FEATURE_FLAG.DOMESTIC_WIRE_OUT,
          analyticsCb: () => {
            eventBus.emit('moveTypeChanged', MoveMoneyTypes.WIRE_OUT);
          },
          disabled: isLoadingInitialData,
        },
        {
          name: 'International Wire Transfer',
          path: '/move-money/international-wire-out',
          id: 'international-wire-out',
          featureFlagId: FEATURE_FLAG.INTERNATIONAL_WIRE_OUT,
          limitedFeatureType: LimitedFeatureType.SendMoneyWireOut,
          analyticsCb: () => {
            eventBus.emit('moveTypeChanged', MoveMoneyTypes.INTERNATIONAL_WIRE_OUT);
          },
          disabled: isLoadingInitialData,
        },
        {
          name: 'Transfer to a Linked Account',
          path: '/send-money',
          id: 'send-money',
          limitedFeatureType: LimitedFeatureType.SendMoneyACHLinkedAccount,
        },
      ],
    },
    {
      linkName: 'Invoicing',
      icon: InvoicesMenuIcon,
      iconSelected: InvoicesMenuIconSelected,
      path: '/invoices',
      id: 'invoices',
      upgradeToUseFeature: true,
      limitedFeatureType: LimitedFeatureType.Invoices,
      featureFlag: FEATURE_FLAG.INVOICE,
      shouldValidateMasterLink: { func: () => validateInvoices() },
      subLinkDropdownIds: ['all-invoices', 'clients', 'services-and-products'],
      subLinks: [
        {
          name: 'All Invoices',
          path: '/invoices',
          id: 'invoices',
          featureFlagId: FEATURE_FLAG.INVOICE,
          limitedFeatureType: LimitedFeatureType.Invoices,
          shouldValidateSubLink: {
            func: (type: string, userSummary: any) => validateClientsAndProductsHub(type, userSummary),
          },
        },
        {
          name: 'Clients List',
          path: '/clients',
          id: 'clients',
          upgradeToUseFeature: false,
          limitedFeatureType: LimitedFeatureType.Invoices,
          featureFlagId: FEATURE_FLAG.INVOICE,
          shouldValidateSubLink: {
            func: (type: string, userSummary: any) => validateClientsAndProductsHub(type, userSummary),
          },
        },
        {
          name: 'Services & Products',
          path: '/services-and-products',
          id: 'services-and-products',
          upgradeToUseFeature: false,
          limitedFeatureType: LimitedFeatureType.Invoices,
          featureFlagId: FEATURE_FLAG.INVOICE,
          shouldValidateSubLink: {
            func: (type: string, userSummary: any) => validateClientsAndProductsHub(type, userSummary),
          },
        },
      ],
    },
    {
      linkName: 'Bill Pay',
      icon: BillPayMenuIcon,
      iconSelected: BillPayMenuIconSelected,
      path: '/bills',
      id: 'bills',
      featureFlag: FEATURE_FLAG.BILL_PAY,
      limitedFeatureType: LimitedFeatureType.BillPay,
      shouldValidateMasterLink: { func: () => validateBillPay() },
    },
    {
      linkName: 'Accountant AI',
      limitedFeatureType: LimitedFeatureType.AccountantAI,
      icon: AiIcon,
      iconSelected: AiIconSelected,
      path: '/accountant-ai',
      id: 'accountant-ai',
      isNewBadge: false,
      shouldValidateMasterLink: { func: () => validateAccountantAi() },
      callback: () =>
        trackMixPanel('Act on menu', {
          action: 'clicking accountant AI on menu',
        }),
    },
    {
      linkName: 'Reports & Statements',
      icon: BankingStatementsIcon,
      iconSelected: BankingStatementsIconSelected,
      path: '/',
      shouldValidateMasterLink: { func: () => validateAccountingReports() },
      id: 'accountingReports',
      limitedFeatureType: LimitedFeatureType.Reports,
      subLinkDropdownIds: ['profit-and-loss', 'cash-flow', 'expenses', 'schedule-c', 'routing-number-change'],
      subLinks: [
        {
          name: 'Profit and Loss',
          path: '/profit-and-loss',
          id: 'profit-and-loss',
          upgradeToUseFeature: true,
          featureFlagId: FEATURE_FLAG.PROFIT_AND_LOSS,
        },
        {
          name: 'Statement of Cash Flow',
          path: '/cash-flow',
          id: 'cash-flow',
          upgradeToUseFeature: true,
          featureFlagId: FEATURE_FLAG.CASH_FLOW,
        },
        {
          name: 'Expense Reports',
          path: '/expenses',
          id: 'expenses',
        },
        {
          name: 'Schedule C',
          path: '/schedule-c',
          id: 'schedule-c',
          shouldValidateSubLink: { func: () => validateFormScheduleC() },
        },
        {
          name: '1065 Form',
          path: '/tax-forms/ScheduleK',
          id: 'tax-forms',
          shouldValidateSubLink: { func: () => validateForm1065() },
        },
        {
          name: '1120-S Form',
          path: '/tax-forms/Form1120s',
          id: 'tax-forms',
          shouldValidateSubLink: { func: () => validateForm1120s() },
        },
        {
          name: '1120 Form',
          path: '/tax-forms/Form1120',
          id: 'tax-forms',
          shouldValidateSubLink: { func: () => validateForm1120() },
        },
        {
          name: 'Statements',
          path: '/statements',
          id: 'statements',
        },
        {
          name: 'Bank Letter',
          path: '',
          id: 'bank-letter',
          callback: async () => {
            await sendBankLetterService();
          },
        },
        {
          name: 'Routing Number Change',
          path: 'routing-number-change',
          id: 'routing-number-change',
          actionFlag: 'allowPayeeMigrationReport',
          featureFlagId: FEATURE_FLAG.PAYEES_MIGRATION_REPORT,
          limitedFeatureType: LimitedFeatureType.BusinessDetails,
          shouldValidateSubLink: {
            func: (type: string, userSummary: any, actionFlag?: string) =>
              actionFlag && validateRoutingNumberChange(userSummary, actionFlag),
          },
        },
      ],
    },
    {
      linkName: 'BalanceUp',
      limitedFeatureType: LimitedFeatureType.BalanceToggle,
      icon: BalanceUpMenuIcon,
      upgradeToUseFeature: false,
      iconSelected: BalanceUpMenuIconSelected,
      path: '/balance-up',
      id: 'balance-up',
      featureFlag: FEATURE_FLAG.BALANCE_UP,
    },
    {
      linkName: 'App Center',
      limitedFeatureType: LimitedFeatureType.AppCenter,
      icon: AppCenterIcon,
      iconSelected: AppCenterIconSelected,
      path: '/app-center',
      id: 'app-center',
    },
  ];

  const [sidebarLinks, setSidebarLinks] = useState<ISidebarLink[]>(generateLinks());

  useEffect(() => {
    setSidebarLinks(generateLinks());
  }, [isLoadingInitialData]);

  return {
    sidebarLinks,
  };
};
